import { showUserAlert } from "../utils/userErrorMessage";
// Header.jsx
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabase/client";
import { useAuth } from "../components/AuthProvider";
import "../mobileOperations.css";

const KAKAO_SALES_SEEN_AT_KEY = "kakaoSalesSeenAt";
const DRIVER_HOLIDAYS_SEEN_AT_KEY = "driverHolidaysSeenAt";
const MAINTENANCE_SEEN_AT_KEY = "maintenanceSeenAt";
const SIGNUP_REQUESTS_SEEN_AT_KEY = "signupRequestsSeenAt";

function NewBadge({ className = "" }) {
  return (
    <span className={`rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white ${className}`}>
      new
    </span>
  );
}

export default function Header() {
  const [showReservationMenu, setShowReservationMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showOperationMenu, setShowOperationMenu] = useState(false);
  const [showOpsMgmtMenu, setShowOpsMgmtMenu] = useState(false); // 운영 관리
  const [showAccountsMenu, setShowAccountsMenu] = useState(false);
  const [showBugMenu, setShowBugMenu] = useState(false); // ✅ 버그 신고
  const accountsTimeoutRef = useRef(null);
  const reservationTimeoutRef = useRef(null);
  const settingsTimeoutRef = useRef(null);
  const opsMgmtTimeoutRef = useRef(null); // 운영 관리
  const operationTimeoutRef = useRef(null);  
  const bugTimeoutRef = useRef(null); 

  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "사용자");
  const companyName = (profile?.company_name || localStorage.getItem("companyName") || "").trim();
  const [hasNewKakaoSales, setHasNewKakaoSales] = useState(false);
  const [hasNewDriverHolidays, setHasNewDriverHolidays] = useState(false);
  const [hasNewMaintenance, setHasNewMaintenance] = useState(false);
  const [hasNewSignupRequests, setHasNewSignupRequests] = useState(false);
  const role = localStorage.getItem("role") || "light";
  const isLight = role === "light";
  const isOperator = role === "operator";
  const canReviewSignupRequests =
    user?.email?.trim().toLowerCase() === "backis87@gmail.com" && profile?.role === "operator";

  useEffect(() => {
    let active = true;
    let pollTimer;

    const loadNewSignupRequests = async () => {
      if (!canReviewSignupRequests) {
        if (active) setHasNewSignupRequests(false);
        return;
      }

      if (location.pathname.startsWith("/signup-requests")) {
        localStorage.setItem(SIGNUP_REQUESTS_SEEN_AT_KEY, new Date().toISOString());
        if (active) setHasNewSignupRequests(false);
        return;
      }

      const seenAt = localStorage.getItem(SIGNUP_REQUESTS_SEEN_AT_KEY) || "";
      const { data, error } = await supabase.functions.invoke("signup-requests", {
        body: { action: "list", status: "pending" },
      });
      if (!active) return;
      if (error) {
        console.error("회원 가입 신청 new 조회 실패:", error.message || error);
        return;
      }

      setHasNewSignupRequests((data?.requests || []).some((request) =>
        !seenAt || (request.created_at && request.created_at > seenAt)
      ));
    };

    loadNewSignupRequests();
    if (canReviewSignupRequests && !location.pathname.startsWith("/signup-requests")) {
      pollTimer = window.setInterval(loadNewSignupRequests, 60000);
    }
    return () => {
      active = false;
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [canReviewSignupRequests, location.pathname]);

  useEffect(() => {
    const loadUserName = async () => {
      const email = localStorage.getItem("userEmail") || localStorage.getItem("email") || "";
      if (!email) return;

      const { data, error } = await supabase
        .from("profiles")
        .select("driver_name")
        .eq("email", email)
        .maybeSingle();

      if (error) {
        console.error("헤더 사용자명 조회 실패:", error.message);
        return;
      }

      const nextName = (data?.driver_name || "").trim();
      if (nextName) {
        localStorage.setItem("userName", nextName);
        setUserName(nextName);
      }
    };

    loadUserName();
    const handleProfileUpdated = (event) => {
      const nextName = String(event?.detail?.driverName || localStorage.getItem("userName") || "사용자").trim();
      if (nextName) setUserName(nextName);
    };
    window.addEventListener("account-profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("account-profile-updated", handleProfileUpdated);
  }, []);

  useEffect(() => {
    const loadNewKakaoSales = async () => {
      const company = localStorage.getItem("email") || "";
      if (!company || isLight) return;

      const since = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString();
      const seenAt = localStorage.getItem(KAKAO_SALES_SEEN_AT_KEY) || "";
      let carsQuery = supabase
        .from("cars")
        .select("car_number, car_number_only");
      if (!isOperator) {
        carsQuery = carsQuery.eq("company", company);
      }

      const [{ data: cars, error: carsError }, { data: requests, error: requestsError }] =
        await Promise.all([
          carsQuery,
          supabase
            .from("bus_booking_requests")
            .select("selected_car_company, selected_car_number, selected_full_car_number, created_at")
            .gte("created_at", since)
            .order("created_at", { ascending: false })
            .limit(100),
        ]);

      if (carsError || requestsError) {
        console.error("카톡 영업 new 조회 실패:", carsError || requestsError);
        return;
      }

      const carKeys = new Set();
      const shortCarNumber = (value = "") => {
        const text = String(value || "").replace(/\s+/g, "").trim();
        const digits = text.replace(/\D/g, "");
        return digits.length >= 4 ? digits.slice(-4) : text;
      };

      (cars || []).forEach((car) => {
        [car.car_number, car.car_number_only].forEach((value) => {
          const key = shortCarNumber(value);
          if (key) carKeys.add(key);
        });
      });

      setHasNewKakaoSales(
        (requests || []).some((request) => {
          if (seenAt && request.created_at && request.created_at <= seenAt) return false;
          if (isOperator) return true;
          if (request.selected_car_company && request.selected_car_company === company) return true;
          return [request.selected_car_number, request.selected_full_car_number]
            .map(shortCarNumber)
            .filter(Boolean)
            .some((key) => carKeys.has(key));
        })
      );
    };

    loadNewKakaoSales();

    const handleSeenAtChange = () => loadNewKakaoSales();
    window.addEventListener("kakao-sales-seen-at-change", handleSeenAtChange);
    return () => window.removeEventListener("kakao-sales-seen-at-change", handleSeenAtChange);
  }, [isLight, isOperator]);

  useEffect(() => {
    const markSeenByPath = () => {
      if (location.pathname.startsWith("/driver-holidays")) {
        localStorage.setItem(DRIVER_HOLIDAYS_SEEN_AT_KEY, new Date().toISOString());
        window.dispatchEvent(new Event("basic-info-new-seen-at-change"));
      }
      if (location.pathname.startsWith("/maintenance")) {
        localStorage.setItem(MAINTENANCE_SEEN_AT_KEY, new Date().toISOString());
        window.dispatchEvent(new Event("basic-info-new-seen-at-change"));
      }
    };

    markSeenByPath();
  }, [location.pathname]);

  useEffect(() => {
    const loadBasicInfoNew = async () => {
      const company = localStorage.getItem("email") || "";
      if (!company || isLight) {
        setHasNewDriverHolidays(false);
        setHasNewMaintenance(false);
        return;
      }

      const driverHolidaysSeenAt = localStorage.getItem(DRIVER_HOLIDAYS_SEEN_AT_KEY) || "";
      const maintenanceSeenAt = localStorage.getItem(MAINTENANCE_SEEN_AT_KEY) || "";
      const since = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString();

      const [driverHolidaysResult, maintenanceResult] = await Promise.all([
        supabase
          .from("holidays_driver")
          .select("id, created_at")
          .eq("company", company)
          .gte("created_at", since)
          .gt("created_at", driverHolidaysSeenAt || "1970-01-01T00:00:00.000Z")
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("maintenance")
          .select("id, created_at")
          .eq("company", company)
          .gte("created_at", since)
          .gt("created_at", maintenanceSeenAt || "1970-01-01T00:00:00.000Z")
          .order("created_at", { ascending: false })
          .limit(1),
      ]);

      if (driverHolidaysResult.error) {
        console.error("기사 휴일 new 조회 실패:", driverHolidaysResult.error.message);
      } else {
        setHasNewDriverHolidays((driverHolidaysResult.data || []).length > 0);
      }

      if (maintenanceResult.error) {
        console.error("고장 신고 new 조회 실패:", maintenanceResult.error.message);
      } else {
        setHasNewMaintenance((maintenanceResult.data || []).length > 0);
      }
    };

    loadBasicInfoNew();
    const handleSeenAtChange = () => loadBasicInfoNew();
    window.addEventListener("basic-info-new-seen-at-change", handleSeenAtChange);
    return () => window.removeEventListener("basic-info-new-seen-at-change", handleSeenAtChange);
  }, [isLight, location.pathname]);

  const hasNewBasicInfo = hasNewDriverHolidays || hasNewMaintenance;

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      showUserAlert("로그아웃에 실패했습니다. 다시 시도해 주세요.");
      return;
    }
    localStorage.clear();
    navigate("/login");
  };

  const handleMouseEnter = (setter, ref) => {
    clearTimeout(ref.current);
    setter(true);
  };

  const handleMouseLeave = (setter, ref) => {
    ref.current = setTimeout(() => setter(false), 200);
  };

  // ✅ 예약 현황 활성 경로에 달력 추가
  const isReservationActive = [
    "/reservation-calendar",
    "/reservations",
    "/general-reservation",
    "/commute-reservation",
    "/reservation-apply",
    "/reservation-search",
    "/off-bus-check",
    "/bus-dispatch-rules",
  ].some((path) => location.pathname.startsWith(path));

  const isOperationActive = ["/operation", "/schedule"].some((path) =>
    location.pathname.startsWith(path)
  );

  const isSettingsActive = [
    "/car-info",
    "/user-management",
    "/sms-sender",
    "/holiday-info",
    "/maintenance",
    "/drivers",
    "/driver-holidays",
  ].some((path) => location.pathname.startsWith(path));

  const isOpsMgmtActive = ["/safety", "/bus-inspections", "/app-notices"].some((path) =>
   location.pathname.startsWith(path)
 );

   const isSellingActive = ["/unpaid", "/salary", "/car-installments", "/car-settlements", "/receipts"
  ].some((path) => location.pathname.startsWith(path));

  const isBugActive = ["/bug-report"].some((path) => // ✅ 버그 신고 활성 체크
    location.pathname.startsWith(path)
  );
  
  const isMonthlyClosingActive = ["/monthly-closing"].some((path) =>
    location.pathname.startsWith(path)
  );

  const isAiApprovalsActive = ["/ai-approvals"].some((path) =>
    location.pathname.startsWith(path)
  );

  const isKakaoSalesActive = ["/kakao-sales"].some((path) =>
    location.pathname.startsWith(path)
  );

  return (
    <div className="relative border-b no-print">
      {/* 🔹 상단 Bus Hub 배너 (배경: bg-gray-100, 가로 꽉 차게, 얇은 높이) */}
      <div className="w-full bg-gray-100">
        <Link to="/reservations" aria-label="배차처리로 이동">
          <img
            src="/images/bus-hub-wide.png"
            alt="Bus Hub"
            className="w-full h-10 md:h-12 object-cover object-left"
          />
        </Link>
      </div>

      {/* 🔹 기존 헤더 바 */}
      <div className="bg-gray-100">
        <div className="hidden md:flex justify-between items-center px-4 py-2">
          <div className="flex gap-4">
            {/* 예약 현황 */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowReservationMenu, reservationTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowReservationMenu, reservationTimeoutRef)}
            >
              <span
                className={`cursor-pointer ${
                  isReservationActive ? "text-blue-600 font-semibold" : "text-gray-700"
                }`}
              >
                예약 현황
              </span>
              {showReservationMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  {/* ✅ 맨 위에 예약 달력 추가 (light도 보임) */}
                  <Link to="/reservation-calendar" className="block px-4 py-2 hover:bg-gray-100">
                    예약 달력
                  </Link>

                  {/* light는 배차처리 안 보이게 */}
                  {!isLight && (
                    <Link to="/reservations" className="block px-4 py-2 hover:bg-gray-100">
                      배차처리
                    </Link>
                  )}
                  <Link to="/reservation-apply" className="block px-4 py-2 hover:bg-gray-100">
                    예약등록
                  </Link>
                  <Link to="/reservation-search" className="block px-4 py-2 hover:bg-gray-100">
                    예약검색
                  </Link>
                  <Link to="/off-bus-check" className="block px-4 py-2 hover:bg-gray-100">
                    휴무버스확인
                  </Link>
                  <Link to="/bus-dispatch-rules" className="block px-4 py-2 hover:bg-gray-100">
                    버스배차규칙
                  </Link>
                </div>
              )}
            </div>

            {/* 미수 관리 */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowAccountsMenu, accountsTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowAccountsMenu, accountsTimeoutRef)}
            >
              <span
                className={`cursor-pointer ${
                  isSellingActive ? "text-blue-600 font-semibold" : "text-gray-700"
                }`}
              >
                매출 관리
              </span>
              {showAccountsMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  <Link to="/unpaid/accounts-receivable" className="block px-4 py-2 hover:bg-gray-100">
                    매출 내역
                  </Link>
                  <Link to="/unpaid/car-expenses" className="block px-4 py-2 hover:bg-gray-100">
                    지출 내역
                  </Link>
                  <Link to="/salary" className="block px-4 py-2 hover:bg-gray-100">
                    급여 내역
                  </Link>
                  <Link to="/car-installments" className="block px-4 py-2 hover:bg-gray-100">
                    할부 내역
                  </Link>
                  <Link to="/car-settlements" className="block px-4 py-2 hover:bg-gray-100">
                    정산 내역
                  </Link>
                  <Link to="/receipts" className="block px-4 py-2 hover:bg-gray-100">
                    영수증 처리 내역
                  </Link>
                </div>
              )}
            </div>

            {/* 운행 내역 */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowOperationMenu, operationTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowOperationMenu, operationTimeoutRef)}
            >
              <span
                className={`cursor-pointer ${
                  isOperationActive ? "text-blue-600 font-semibold" : "text-gray-700"
                }`}
              >
                운행 내역
              </span>
              {showOperationMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  <Link to="/operation" className="block px-4 py-2 hover:bg-gray-100">
                    운행 현황표
                  </Link>
                  <Link to="/schedule" className="block px-4 py-2 hover:bg-gray-100">
                    배차 일정표
                  </Link>
                </div>
              )}
            </div>

            {/* 운영 관리 */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowOpsMgmtMenu, opsMgmtTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowOpsMgmtMenu, opsMgmtTimeoutRef)}
            >
              <span
                className={`cursor-pointer ${
                  isOpsMgmtActive ? "text-blue-600 font-semibold" : "text-gray-700"
                }`}
              >
                운영 관리
              </span>
              {showOpsMgmtMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  <Link to="/safety" className="block px-4 py-2 hover:bg-gray-100">
                    안전 관리
                  </Link>
                  <Link to="/bus-inspections" className="block px-4 py-2 hover:bg-gray-100">
                    버스 검사 예약 관리
                  </Link>
                  <Link to="/app-notices" className="block px-4 py-2 hover:bg-gray-100">
                    앱 공지사항
                  </Link>
                </div>
              )}
            </div>

            {/* 기본 정보 */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowSettingsMenu, settingsTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowSettingsMenu, settingsTimeoutRef)}
            >
              <span
                className={`relative cursor-pointer ${
                  isSettingsActive ? "text-blue-600 font-semibold" : "text-gray-700"
                }`}
              >
                기본 정보
                {hasNewBasicInfo && <NewBadge className="absolute left-1/2 top-[-14px] -translate-x-1/2" />}
              </span>
              {showSettingsMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  <Link to="/car-info" className="block px-4 py-2 hover:bg-gray-100">차량 정보</Link>
                  <Link to="/drivers" className="block px-4 py-2 hover:bg-gray-100">기사 정보</Link>
                  <Link to="/driver-holidays" className="flex items-center justify-between px-4 py-2 hover:bg-gray-100">
                    <span>기사 휴일</span>
                    {hasNewDriverHolidays && <NewBadge />}
                  </Link>
                  <Link to="/user-management" className="block px-4 py-2 hover:bg-gray-100">사용자관리</Link>
                  <Link to="/holiday-info" className="block px-4 py-2 hover:bg-gray-100">휴일정보</Link>
                  <Link to="/sms-sender" className="block px-4 py-2 hover:bg-gray-100">SMS문자발신번호</Link>
                  <Link to="/maintenance" className="flex items-center justify-between px-4 py-2 hover:bg-gray-100">
                    <span>고장 신고</span>
                    {hasNewMaintenance && <NewBadge />}
                  </Link>
                </div>
              )}
            </div>

          {/* ✅ 버그 신고 (맨 오른쪽 추가) */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter(setShowBugMenu, bugTimeoutRef)}
              onMouseLeave={() => handleMouseLeave(setShowBugMenu, bugTimeoutRef)}
            >
              <span
                className={`cursor-pointer ${isBugActive ? "text-blue-600 font-semibold" : "text-gray-700"}`}
              >
                버그 신고
              </span>
              {showBugMenu && (
                <div className="absolute z-[100] left-0 mt-1 bg-white border rounded shadow-md w-48">
                  <Link to="/bug-report" className="block px-4 py-2 hover:bg-gray-100">
                    버그 신고
                  </Link>
                </div>
              )}
            </div>
                      {/* ✅ AI 월말결산 (맨 끝 추가) */}
            <Link
              to="/monthly-closing"
              className={`cursor-pointer ${
                isMonthlyClosingActive ? "text-blue-600 font-semibold" : "text-gray-700"
              }`}
            >
              AI 월말결산
            </Link>
            <Link
              to="/ai-approvals"
              className={`cursor-pointer ${
                isAiApprovalsActive ? "text-blue-600 font-semibold" : "text-gray-700"
              }`}
            >
              AI 승인함
            </Link>
            <Link
              to="/kakao-sales"
              className={`relative cursor-pointer pr-5 ${
                isKakaoSalesActive ? "text-blue-600 font-semibold" : "text-gray-700"
              }`}
            >
              카톡 영업
              {hasNewKakaoSales && (
                <NewBadge className="absolute -right-1 -top-2" />
              )}
            </Link>
            {canReviewSignupRequests && (
              <Link
                to="/signup-requests"
                className={`relative cursor-pointer pr-5 ${
                  location.pathname.startsWith("/signup-requests")
                    ? "text-blue-600 font-semibold"
                    : "text-gray-700"
                }`}
              >
                회원 가입 신청
                {hasNewSignupRequests && (
                  <NewBadge className="absolute -right-1 -top-2" />
                )}
              </Link>
            )}
          </div>

            


          {/* 사용자 정보 및 로그아웃 */}
          <div className="flex items-center gap-3">
            {companyName && <span className="text-sm text-gray-600">{companyName}</span>}
            <Link to="/my-account" className="text-sm text-gray-600 hover:text-blue-600 hover:underline">
              {userName}님
            </Link>
            <button onClick={handleLogout} className="text-red-500">로그아웃</button>
          </div>
        </div>
        <div className="md:hidden px-3 py-2">
          <nav aria-label="모바일 메뉴" className="grid grid-cols-4 gap-1 text-center text-xs">
            {[
              { to: "/reservations", label: "배차처리", active: location.pathname.startsWith("/reservations") },
              { to: "/schedule", label: "배차 일정표", active: location.pathname.startsWith("/schedule") },
              { to: "/maintenance", label: "고장 신고", active: location.pathname.startsWith("/maintenance") },
              { to: "/ai-approvals", label: "AI 승인함", active: isAiApprovalsActive },
            ].filter((item) => !isLight || item.to !== "/reservations").map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded px-1 py-2 ${item.active ? "bg-blue-600 font-semibold text-white" : "text-gray-700"}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-600">
            {companyName && <span className="max-w-[45%] truncate">{companyName}</span>}
            <Link to="/my-account" className="max-w-[30%] truncate hover:text-blue-600 hover:underline">
              {userName}님
            </Link>
            <button onClick={handleLogout} className="shrink-0 text-red-600">로그아웃</button>
          </div>
        </div>
      </div>
    </div>
  );
}
